import React, { useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";

type Props = {
    value: string;
    onChange: (html: string) => void;
    onUploadImage: (file: File) => Promise<string>;
};

// Кастомное расширение Image с возможностью изменения размера
const ResizableImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: (element) => element.getAttribute("width"),
                renderHTML: (attributes) => {
                    if (!attributes.width) {
                        return {};
                    }
                    return {
                        width: attributes.width,
                    };
                },
            },
            height: {
                default: null,
                parseHTML: (element) => element.getAttribute("height"),
                renderHTML: (attributes) => {
                    if (!attributes.height) {
                        return {};
                    }
                    return {
                        height: attributes.height,
                    };
                },
            },
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute("style"),
                renderHTML: (attributes) => {
                    if (!attributes.style) {
                        return {};
                    }
                    return {
                        style: attributes.style,
                    };
                },
            },
        };
    },
    addNodeView() {
        return ({ node, HTMLAttributes, getPos, editor }) => {
            const dom = document.createElement("div");
            dom.className = "image-wrapper relative inline-block group my-4";
            dom.style.display = "block";
            dom.style.maxWidth = "100%";
            dom.draggable = true;

            const img = document.createElement("img");
            Object.entries(HTMLAttributes).forEach(([key, value]) => {
                if (key === "style" && typeof value === "string") {
                    img.style.cssText = value;
                } else if (key !== "class") {
                    img.setAttribute(key, value);
                }
            });

            if (node.attrs.src) {
                img.src = node.attrs.src;
            }
            if (node.attrs.alt) {
                img.alt = node.attrs.alt;
            }
            if (node.attrs.width) {
                img.width = parseInt(node.attrs.width);
            }
            if (node.attrs.height) {
                img.height = parseInt(node.attrs.height);
            }

            img.style.maxWidth = "100%";
            img.style.height = "auto";
            img.style.display = "block";
            img.style.cursor = "move";

            // Контролы для изменения размера и управления
            const controls = document.createElement("div");
            controls.className =
                "absolute top-0 right-0 bg-blue-600 text-white text-xs p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 items-center";
            controls.style.zIndex = "10";

            // Поле для ширины
            const widthInput = document.createElement("input");
            widthInput.type = "number";
            widthInput.placeholder = "Ширина";
            widthInput.className = "w-16 px-1 py-0.5 text-xs border rounded text-gray-900";
            widthInput.value = node.attrs.width || "";

            // Предотвращаем всплытие событий, чтобы можно было кликнуть и ввести значение
            widthInput.addEventListener("click", (e) => {
                e.stopPropagation();
                widthInput.focus();
            });
            widthInput.addEventListener("mousedown", (e) => {
                e.stopPropagation();
            });
            widthInput.addEventListener("focus", (e) => {
                e.stopPropagation();
            });

            widthInput.addEventListener("change", (e) => {
                e.stopPropagation();
                const width = (e.target as HTMLInputElement).value;
                if (getPos && typeof getPos === "function") {
                    const pos = getPos();
                    if (pos !== null && pos !== undefined) {
                        editor
                            .chain()
                            .setNodeSelection(pos)
                            .updateAttributes("image", { width: width || null })
                            .run();
                    }
                }
            });

            // Также обновляем при вводе (не только при blur)
            widthInput.addEventListener("input", (e) => {
                e.stopPropagation();
                const width = (e.target as HTMLInputElement).value;
                if (getPos && typeof getPos === "function") {
                    const pos = getPos();
                    if (pos !== null && pos !== undefined) {
                        editor
                            .chain()
                            .setNodeSelection(pos)
                            .updateAttributes("image", { width: width || null })
                            .run();
                    }
                }
            });

            // Кнопка удаления
            const deleteBtn = document.createElement("button");
            deleteBtn.textContent = "×";
            deleteBtn.className =
                "px-2 py-0.5 bg-red-500 text-white rounded text-xs hover:bg-red-600";
            deleteBtn.title = "Удалить изображение";
            deleteBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                if (getPos && typeof getPos === "function") {
                    const pos = getPos();
                    if (pos !== null && pos !== undefined) {
                        editor.chain().setNodeSelection(pos).deleteSelection().run();
                    }
                }
            });

            controls.appendChild(widthInput);
            controls.appendChild(deleteBtn);
            dom.appendChild(img);
            dom.appendChild(controls);

            // Drag & Drop для перемещения изображения
            let dragStartPos: number | null | undefined = null;
            dom.addEventListener("dragstart", (e) => {
                if (getPos && typeof getPos === "function") {
                    const pos = getPos();
                    dragStartPos = pos ?? null;
                    if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed = "move";
                    }
                }
            });

            dom.addEventListener("dragover", (e) => {
                e.preventDefault();
                if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = "move";
                }
            });

            return {
                dom,
            };
        };
    },
});

const NewsEditor: React.FC<Props> = ({ value, onChange, onUploadImage }) => {
    const [isHtmlMode, setIsHtmlMode] = useState(false);
    const [htmlContent, setHtmlContent] = useState(value || "");

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Отключаем Link из StarterKit, так как добавляем свой с настройками
                link: false,
            }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
            }),
            ResizableImage.configure({
                inline: false,
                allowBase64: false,
            }),
        ],
        content: value || "",
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange(html);
            setHtmlContent(html);
        },
    });

    React.useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if ((value || "") !== current && !isHtmlMode) {
            editor.commands.setContent(value || "", { emitUpdate: false });
            setHtmlContent(value || "");
        }
    }, [value, editor, isHtmlMode]);

    const handleHtmlChange = (newHtml: string) => {
        setHtmlContent(newHtml);
        onChange(newHtml);
        if (editor && !isHtmlMode) {
            editor.commands.setContent(newHtml, { emitUpdate: false });
        }
    };

    const toggleMode = () => {
        if (isHtmlMode) {
            // Переключаемся на визуальный режим
            if (editor) {
                editor.commands.setContent(htmlContent, { emitUpdate: false });
            }
        } else {
            // Переключаемся на HTML режим
            if (editor) {
                setHtmlContent(editor.getHTML());
            }
        }
        setIsHtmlMode(!isHtmlMode);
    };

    const uploadAndInsertImage = async (file: File) => {
        if (!editor) return;
        const url = await onUploadImage(file);
        editor.chain().focus().setImage({ src: url }).run();
    };

    return (
        <div className="border rounded bg-white">
            <div className="flex flex-wrap items-center gap-2 p-2 border-b bg-gray-50">
                <button
                    type="button"
                    className={`px-2 py-1 rounded border text-sm ${
                        isHtmlMode
                            ? "bg-blue-500 text-white"
                            : "bg-white hover:bg-gray-100"
                    }`}
                    onClick={toggleMode}
                >
                    {isHtmlMode ? "Визуальный" : "HTML"}
                </button>
                {!isHtmlMode && (
                    <>
                        <button
                            type="button"
                            className="px-2 py-1 rounded border bg-white text-sm hover:bg-gray-100"
                            onClick={() => editor?.chain().focus().toggleBold().run()}
                        >
                            <strong>B</strong>
                        </button>
                        <button
                            type="button"
                            className="px-2 py-1 rounded border bg-white text-sm hover:bg-gray-100"
                            onClick={() => editor?.chain().focus().toggleItalic().run()}
                        >
                            <em>I</em>
                        </button>
                        <button
                            type="button"
                            className="px-2 py-1 rounded border bg-white text-sm hover:bg-gray-100"
                            onClick={() =>
                                editor?.chain().focus().toggleHeading({ level: 2 }).run()
                            }
                        >
                            H2
                        </button>
                        <button
                            type="button"
                            className="px-2 py-1 rounded border bg-white text-sm hover:bg-gray-100"
                            onClick={() =>
                                editor?.chain().focus().toggleBulletList().run()
                            }
                        >
                            • List
                        </button>
                        <button
                            type="button"
                            className="px-2 py-1 rounded border bg-white text-sm hover:bg-gray-100"
                            onClick={() =>
                                editor?.chain().focus().toggleOrderedList().run()
                            }
                        >
                            1. List
                        </button>
                        <button
                            type="button"
                            className="px-2 py-1 rounded border bg-white text-sm hover:bg-gray-100"
                            onClick={() => {
                                const url = window.prompt("Введите URL ссылки:");
                                if (url) {
                                    const { from, to } = editor?.state.selection || {};
                                    const selectedText = editor?.state.doc.textBetween(from || 0, to || 0);

                                    if (selectedText) {
                                        // Если есть выделенный текст, делаем его ссылкой
                                        editor?.chain().focus().setLink({ href: url }).run();
                                    } else {
                                        // Если нет выделенного текста, вставляем ссылку
                                        editor?.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
                                    }
                                }
                            }}
                        >
                            🔗 Link
                        </button>
                        <button
                            type="button"
                            className="px-2 py-1 rounded border bg-white text-sm hover:bg-gray-100"
                            onClick={() => {
                                editor?.chain().focus().unsetLink().run();
                            }}
                            title="Удалить ссылку"
                        >
                            🔗×
                        </button>
                        <label className="px-2 py-1 rounded border bg-white text-sm cursor-pointer hover:bg-gray-100">
                            📷 Image
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    e.target.value = "";
                                    await uploadAndInsertImage(file);
                                }}
                            />
                        </label>
                    </>
                )}
            </div>

            {isHtmlMode ? (
                <div className="p-3">
                    <textarea
                        value={htmlContent}
                        onChange={(e) => handleHtmlChange(e.target.value)}
                        className="w-full h-96 p-3 font-mono text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Введите HTML-код..."
                        spellCheck={false}
                    />
                    <div className="mt-2 text-xs text-gray-500">
                        Редактирование исходного HTML. Изменения применяются автоматически.
                    </div>
                </div>
            ) : (
                <div className="p-3">
                    <EditorContent editor={editor} />
                    <style>{`
                        .ProseMirror {
                            outline: none;
                            min-height: 200px;
                        }
                        .ProseMirror img {
                            max-width: 100%;
                            height: auto;
                            display: block;
                            margin: 1rem 0;
                        }
                        .ProseMirror .image-wrapper {
                            position: relative;
                            display: block;
                            margin: 1rem 0;
                            border: 2px dashed transparent;
                            border-radius: 4px;
                            padding: 4px;
                        }
                        .ProseMirror .image-wrapper:hover {
                            border-color: #3b82f6;
                        }
                        .ProseMirror .image-wrapper:hover .absolute {
                            opacity: 1;
                        }
                        .ProseMirror .image-wrapper[draggable="true"] {
                            cursor: move;
                        }
                        .ProseMirror .image-wrapper[draggable="true"]:active {
                            opacity: 0.5;
                        }
                    `}</style>
                </div>
            )}
        </div>
    );
};

export default NewsEditor;


